import { useState } from 'react';
import { Timer, X, Play, Square } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Timer Modal for Virtual Classroom
 * Allows teacher to set a timer visible to all participants
 */
const TimerModal = ({
  isOpen,
  onClose,
  timerActive,
  timerRemainingSeconds,
  onStartTimer,
  onStopTimer,
  formatTime,
}) => {
  const { t } = useTranslation();
  const [customMinutes, setCustomMinutes] = useState(5);

  if (!isOpen) return null;

  const handleStartTimer = (minutes) => {
    onStartTimer(minutes);
    onClose();
  };

  const handleStopTimer = () => {
    onStopTimer();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Timer size={20} className="text-brand-primary" />
            {t('classroom.timerTitle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={20} />
          </button>
        </div>

        {timerActive ? (
          <div className="text-center py-4">
            <p className="text-gray-600 mb-3">{t('classroom.timerActive')}</p>
            <div className="text-4xl font-mono font-bold text-brand-primary mb-4">
              {formatTime(timerRemainingSeconds)}
            </div>
            <button
              onClick={handleStopTimer}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2"
            >
              <Square size={18} />
              {t('classroom.timerStop')}
            </button>
          </div>
        ) : (
          <>
            <p className="text-gray-600 text-sm mb-4">
              {t('classroom.timerPrompt')}
            </p>

            {/* Quick presets */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[1, 2, 3, 5].map((min) => (
                <button
                  key={min}
                  onClick={() => handleStartTimer(min)}
                  className="bg-gray-100 hover:bg-brand-sage/30 text-gray-700 hover:text-brand-primary py-3 rounded-lg font-medium transition"
                >
                  {min} {t('classroom.timerMinutesShort')}
                </button>
              ))}
            </div>

            {/* Custom time */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classroom.timerCustomLabel')}
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(Math.max(1, Math.min(60, parseInt(e.target.value) || 1)))}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-center text-lg font-medium focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
                <button
                  onClick={() => handleStartTimer(customMinutes)}
                  className="bg-brand-primary hover:bg-brand-primary-hover text-white px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
                >
                  <Play size={18} />
                  {t('classroom.timerStart')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TimerModal;
